"use client"

import React, { useState, useEffect } from 'react'
import { Filter, Search, AlertTriangle, X, ChevronDown, RefreshCw, Check } from 'lucide-react'
import {
  Badge,
  Button,
  Checkbox,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from "@mantis-sas/ui";

export default function InventoryFilterBar({ 
  searchTerm, 
  onSearchChange, 
  filters,
  onFilterChange,
  onRefresh,
  itemCount,
  totalCount,
  categories
}) {
  const [tempFilters, setTempFilters] = useState(filters)
  const [subcategories, setSubcategories] = useState([])
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)

  // Update temp filters when props change
  useEffect(() => {
    setTempFilters(filters)
  }, [filters])

  // Update subcategories when category changes
  useEffect(() => {
    if (tempFilters.category && categories) {
      const category = categories.find(cat => cat.id === tempFilters.category)
      setSubcategories(category?.subcategories || [])
      if (!category?.subcategories?.find(sub => sub.id === tempFilters.subcategory)) {
        setTempFilters(prev => ({ ...prev, subcategory: null }))
      }
    } else {
      setSubcategories([])
      setTempFilters(prev => ({ ...prev, subcategory: null }))
    }
  }, [tempFilters.category, tempFilters.subcategory, categories])

  // Calculate active filters count
  const activeFilterCount = Object.values(tempFilters).filter(value => {
    return value !== null && value !== false && value !== undefined && value !== ""
  }).length

  // Apply filters
  const applyFilters = () => {
    onFilterChange(tempFilters)
  }

  // Clear all filters
  const clearFilters = () => {
    const clearedFilters = {
      lowStock: false,
      category: null,
      subcategory: null
    }
    setTempFilters(clearedFilters)
    onFilterChange(clearedFilters)
  }

  // Handle category change
  const handleCategoryChange = (value) => {
    const newFilters = {
      ...tempFilters,
      category: value === "" ? null : value,
      subcategory: null
    }
    setTempFilters(newFilters)
  }

  // Handle subcategory change
  const handleSubcategoryChange = (value) => {
    setTempFilters(prev => ({
      ...prev,
      subcategory: value === "" ? null : value
    }))
  }

  // Handle low stock toggle
  const handleLowStockToggle = (checked) => {
    setTempFilters(prev => ({
      ...prev,
      lowStock: checked
    }))
  }

  const FiltersContent = () => (
    <div className="space-y-4">
      {/* Quick Filters */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">
          Filtros Rápidos
        </h4>
        
        <div className="flex items-center space-x-2">
          <Checkbox
            id="lowStock"
            checked={tempFilters.lowStock}
            onCheckedChange={handleLowStockToggle}
            className="rounded"
          />
          <Label 
            htmlFor="lowStock" 
            className="text-sm text-slate-700 dark:text-slate-300 cursor-pointer flex items-center"
          >
            <AlertTriangle className="h-4 w-4 text-amber-500 mr-2" />
            Artículos con poco stock
          </Label>
        </div>
      </div>

      <Separator />

      {/* Category Filters */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">
          Categorización
        </h4>
        
        <div className="space-y-3">
          {/* Category */}
          <div className="space-y-2">
            <Label className="text-xs text-slate-600 dark:text-slate-400">
              Categoría
            </Label>
            <Select
              value={tempFilters.category || ""}
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger className="w-full rounded border-slate-300 dark:border-slate-600">
                <SelectValue placeholder="Todas las categorías" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Todas las categorías</SelectItem>
                {categories?.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Subcategory */}
          {subcategories.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-slate-600 dark:text-slate-400">
                Subcategoría
              </Label>
              <Select
                value={tempFilters.subcategory || ""}
                onValueChange={handleSubcategoryChange}
              >
                <SelectTrigger className="w-full rounded border-slate-300 dark:border-slate-600">
                  <SelectValue placeholder="Todas las subcategorías" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todas las subcategorías</SelectItem>
                  {subcategories.map((subcategory) => (
                    <SelectItem key={subcategory.id} value={subcategory.id}>
                      {subcategory.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      <Separator />

      {/* Filter Actions */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={clearFilters}
          className="flex-1 rounded border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300"
        >
          <X className="h-4 w-4 mr-2" />
          Limpiar
        </Button>
        <Button 
          onClick={applyFilters}
          size="sm"
          className="flex-1 bg-orange-500 hover:bg-orange-600 text-white rounded"
        >
          <Check className="h-4 w-4 mr-2" />
          Aplicar filtros
        </Button>
      </div>
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Main Filter Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        {/* Search Input */}
        <div className="relative flex-1 w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Buscar elementos de inventario..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800"
          />
        </div>

        {/* Filter Controls */}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Desktop Filters */}
          <div className="hidden sm:flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  <span>Filtros</span>
                  {activeFilterCount > 0 && (
                    <Badge className="ml-2 bg-orange-500 text-white text-xs rounded">
                      {activeFilterCount}
                    </Badge>
                  )}
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent 
                className="w-80 p-4 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded shadow-lg"
                align="end"
              >
                <FiltersContent />
              </PopoverContent>
            </Popover>
          </div>

          {/* Mobile Filters Toggle */}
          <div className="sm:hidden flex-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="w-full rounded border-slate-300 dark:border-slate-600 justify-between"
            >
              <div className="flex items-center">
                <Filter className="h-4 w-4 mr-2" />
                <span>Filtros</span>
                {activeFilterCount > 0 && (
                  <Badge className="ml-2 bg-orange-500 text-white text-xs rounded">
                    {activeFilterCount}
                  </Badge>
                )}
              </div>
              <ChevronDown 
                className={`h-4 w-4 transition-transform duration-200 ${
                  showAdvancedFilters ? 'rotate-180' : ''
                }`} 
              />
            </Button>
          </div>

          {/* Refresh Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            className="rounded border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Mobile Filters Panel */}
      {showAdvancedFilters && (
        <div className="sm:hidden bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded p-4">
          <FiltersContent />
        </div>
      )}

      {/* Active Filters Display */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-600 dark:text-slate-400">
            Filtros activos:
          </span>
          
          {tempFilters.lowStock && (
            <Badge variant="secondary" className="rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Poco stock
              <button
                onClick={() => handleLowStockToggle(false)}
                className="ml-2 hover:bg-amber-200 dark:hover:bg-amber-800 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          
          {tempFilters.category && (
            <Badge variant="secondary" className="rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
              {categories?.find(c => c.id === tempFilters.category)?.name}
              <button
                onClick={() => handleCategoryChange("")}
                className="ml-2 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          
          {tempFilters.subcategory && (
            <Badge variant="secondary" className="rounded bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
              {subcategories?.find(s => s.id === tempFilters.subcategory)?.name}
              <button
                onClick={() => handleSubcategoryChange("")}
                className="ml-2 hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded"
          >
            <X className="h-4 w-4 mr-1" />
            Limpiar todo
          </Button>
        </div>
      )}

      {/* Results Summary */}
      {itemCount !== totalCount && (
        <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            <span className="text-sm text-slate-600 dark:text-slate-400">
              Mostrando <span className="font-medium text-slate-900 dark:text-slate-100">{itemCount}</span> de{' '}
              <span className="font-medium text-slate-900 dark:text-slate-100">{totalCount}</span> elementos
            </span>
          </div>
          
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:text-orange-400 dark:hover:text-orange-300 dark:hover:bg-orange-900/20 rounded"
            >
              Ver todos
            </Button>
          )}
        </div>
      )}
    </div>
  )
} 